import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, User, Send, CheckCircle, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        setIsSuccess(true);
        toast.success('Thank you! Your message has been sent successfully.');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: ''
          });
          setIsSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="py-20 px-4" id="contact">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white border-2 border-black shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#ECEC75] rounded-full mb-6">
                <CheckCircle className="h-10 w-10 text-black" />
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Crimson Text', serif" }}>
                Thank You!
              </h3>
              <p className="text-xl text-gray-700">
                Your message has been sent successfully. I'll get back to you within 24 hours.
              </p>
              <div className="mt-8">
                <Button
                  onClick={() => window.open('https://calendly.com/digitalcoachshyam/startegy-call', '_blank')}
                  className="bg-black text-white hover:bg-gray-800 px-8 py-3 font-semibold rounded-lg transition-all duration-200"
                >
                  Or Book a Call Directly
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-4" id="contact">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: "'Crimson Text', serif" }}>
            Get In Touch
          </h2>
          <p className="text-xl text-gray-700">
            Have a project in mind? Let's discuss how I can help you achieve your marketing goals.
          </p>
        </div>

        <Card className="bg-white border-2 border-gray-200 hover:border-black transition-colors duration-300 shadow-xl">
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: "'Crimson Text', serif" }}>
              Send Me a Message
            </CardTitle>
            <CardDescription className="text-base">
              Fill out the form below and I'll get back to you within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-semibold">
                    Your Name *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 py-6 border-2 border-gray-200 focus:border-black transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-semibold">
                    Email Address *
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 py-6 border-2 border-gray-200 focus:border-black transition-colors"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base font-semibold">
                  Phone Number *
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 py-6 border-2 border-gray-200 focus:border-black transition-colors"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-base font-semibold">
                  Your Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell me about your project, goals, and how I can help..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="border-2 border-gray-200 focus:border-black transition-colors resize-none"
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-4">
                <p className="text-sm text-gray-600">
                  * All fields are required
                </p>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-200 hover:translate-y-[-2px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-8 pt-8 border-t-2 border-gray-100">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <Mail className="h-6 w-6 mx-auto mb-2 text-gray-700" />
                  <p className="text-sm font-semibold text-gray-900">Email</p>
                  <a href="mailto:digitalcoachshyam@gmail.com" className="text-sm text-gray-600 hover:text-black transition-colors">
                    digitalcoachshyam@gmail.com
                  </a>
                </div>
                <div>
                  <Phone className="h-6 w-6 mx-auto mb-2 text-gray-700" />
                  <p className="text-sm font-semibold text-gray-900">Phone</p>
                  <a href="tel:+919867809539" className="text-sm text-gray-600 hover:text-black transition-colors">
                    +91 98678 09539
                  </a>
                </div>
                <div>
                  <MessageCircle className="h-6 w-6 mx-auto mb-2 text-[#25D366]" />
                  <p className="text-sm font-semibold text-gray-900">WhatsApp</p>
                  <button 
                    onClick={() => {
                      const message = encodeURIComponent('Hi! I came across your Digital Shyaam website and would like to discuss my marketing needs.');
                      window.open(`https://wa.me/919867809539?text=${message}`, '_blank');
                    }}
                    className="text-sm text-gray-600 hover:text-[#25D366] transition-colors"
                  >
                    Chat Now
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactForm;
